import type { EmaLogoViewModelMaybe, ProductInformationViewModel } from 'autogen/swagger/all';
import Layout from 'components/Layout';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import { Spacing24, Spacing32, Spacing4 } from 'autogen/design-tokens/tokens';
import ProductItemGallery from 'components/product/ProductItemGallery/ProductItemGallery';
import ProductAccordion from 'components/product/accordion';
import OTCNotification from 'components/product/components/OTCNotification';
import MidSection from './MidSection/MidSection';
import HazardLabel from './MidSection/components/HazardLabel';
import SplashTagLines from './MidSection/components/SplashTagLines';

const Container = styled.div`
    display: flex;
    row-gap: ${Spacing24};
    flex-direction: column;

    ${breakpoint.up(sizes.lg)} {
        justify-content: space-between;
        column-gap: ${Spacing32};
        flex-direction: row;
    }
    ${breakpoint.up(sizes.xl)} {
        column-gap: 8.5%;
    }
`;

const ImageColumn = styled(Layout.Flex.Column)`
    ${breakpoint.up(sizes.lg)} {
        position: relative;
        flex: 1 1 56.5%;
    }
`;

const Sticky = styled(Layout.Flex.Column)`
    ${breakpoint.up(sizes.lg)} {
        position: sticky;
        top: ${Spacing32};
    }
`;

const InformationColumn = styled(Layout.Flex.Column)`
    ${breakpoint.up(sizes.lg)} {
        min-width: 350px;
        flex: 1 1 35%;
    }
`;

const StyledSplashTagLines = styled(SplashTagLines)`
    margin-bottom: ${Spacing4};

    ${breakpoint.up(sizes.lg)} {
        display: none;
    }
`;

export default function ProductInformation({
    productInformation,
    emaLogo,
    ...restProps
}: {
    productInformation: ProductInformationViewModel;
    emaLogo?: EmaLogoViewModelMaybe;
}) {
    const { productMedia, splashes, otcWarning, hazarlabelUrls } = productInformation;

    return (
        <Container {...restProps}>
            {/* Product Image */}
            <ImageColumn>
                <Sticky gap={{ xs: Spacing24 }}>
                    <div>
                        <StyledSplashTagLines splashes={splashes} />

                        <ProductItemGallery items={productMedia} splashes={splashes} emaLogo={emaLogo} />
                    </div>

                    {otcWarning && <OTCNotification model={otcWarning} />}

                    {!!hazarlabelUrls.length && <HazardLabel model={hazarlabelUrls} />}
                </Sticky>
            </ImageColumn>

            {/* Product info + Buying Options + Product accordion */}
            <InformationColumn gap={{ xs: Spacing24, lg: Spacing32 }}>
                <MidSection model={productInformation} />

                <ProductAccordion model={productInformation} />
            </InformationColumn>
        </Container>
    );
}
